/**
 * 방문요양 급여 — 공제 항목 고정(체크) 정책
 * - 미체크: 자동 계산(「계산」·연쇄 산출)
 * - 체크: 입력값 고정, 입력 클릭 시 모달로 금액 설정
 * - c_out(퇴직금): cwhd_apply_retirement 별도 (기존 유지)
 */

import type { HomeWagePayrollFieldKey } from './homeWagePayrollFields';

/** DB *_fixed 컬럼이 있는 공제 금액 필드 */
export type HomeWageDeductionFixFieldKey =
  | 'l_yeon'
  | 'l_gun'
  | 'l_jang'
  | 'l_go'
  | 'l_so'
  | 'l_ji'
  | 'l_jiwon'
  | 'c_yeon'
  | 'c_gun'
  | 'c_jang'
  | 'c_go'
  | 'c_sil'
  | 'c_san';

export type HomeWageDeductionFixedFlags = Record<HomeWageDeductionFixFieldKey, boolean>;

export const HOME_WAGE_DEDUCTION_FIX_FIELD_KEYS: HomeWageDeductionFixFieldKey[] = [
  'l_yeon',
  'l_gun',
  'l_jang',
  'l_go',
  'l_so',
  'l_ji',
  'l_jiwon',
  'c_yeon',
  'c_gun',
  'c_jang',
  'c_go',
  'c_sil',
  'c_san',
];

/** 4대보험 쌍 — 근로·기관 동시 고정 */
export const HOME_WAGE_INSURANCE_PAIR_FIX_KEYS = ['yeon', 'gun', 'jang'] as const;

export type HomeWageInsurancePairFixKey = (typeof HOME_WAGE_INSURANCE_PAIR_FIX_KEYS)[number];

export function createEmptyHomeWageDeductionFixedFlags(): HomeWageDeductionFixedFlags {
  return {
    l_yeon: false,
    l_gun: false,
    l_jang: false,
    l_go: false,
    l_so: false,
    l_ji: false,
    l_jiwon: false,
    c_yeon: false,
    c_gun: false,
    c_jang: false,
    c_go: false,
    c_sil: false,
    c_san: false,
  };
}

export function parseHomeWageDeductionFixedFlag(value: unknown): boolean {
  return value === 'Y' || value === true;
}

export function formatHomeWageDeductionFixedFlag(fixed: boolean): 'Y' | 'N' {
  return fixed ? 'Y' : 'N';
}

/** 폼 key → DB fixed 컬럼 suffix (l_yeon → l_yeon_fixed) */
export function homeWageDeductionFixedDbKey(fieldKey: HomeWageDeductionFixFieldKey): string {
  return `${fieldKey}_fixed`;
}

export function isHomeWageDeductionFixFieldKey(
  key: HomeWagePayrollFieldKey
): key is HomeWageDeductionFixFieldKey {
  return (HOME_WAGE_DEDUCTION_FIX_FIELD_KEYS as string[]).includes(key);
}

/** 4대보험 공유 입력 쌍 — labor/corp 동시 토글 */
export function setInsurancePairFixedFlags(
  flags: HomeWageDeductionFixedFlags,
  pairKey: HomeWageInsurancePairFixKey,
  checked: boolean
): void {
  const laborKey = `l_${pairKey}` as HomeWageDeductionFixFieldKey;
  const corpKey = `c_${pairKey}` as HomeWageDeductionFixFieldKey;
  flags[laborKey] = checked;
  flags[corpKey] = checked;
}

export function isInsurancePairFixed(
  flags: HomeWageDeductionFixedFlags,
  pairKey: HomeWageInsurancePairFixKey
): boolean {
  return flags[`l_${pairKey}` as HomeWageDeductionFixFieldKey] || false;
}

export const HOME_WAGE_DEDUCTION_FIX_CHECKBOX_TOOLTIP = [
  '체크 시 「계산」(지난달 데이터 가져오기 포함) 시',
  '해당 금액은 자동계산으로 덮어쓰지 않습니다.',
  '입력란 클릭 → 금액 설정 모달',
].join('\n');

/** 모달 자동 산출 미리보기 */
export interface HomeWageDeductionAutoPreview {
  amount: number;
  hasAuto: boolean;
  /** 상단 안내 (항목별 추가 설명) */
  description?: string;
  /** 산출 근거·과정 (여러 줄) */
  formula: string;
  /** 면제·주의 (있을 때만) */
  notes?: string;
}
